import React, { useEffect, useState } from "react";
import * as Dialog from "@radix-ui/react-dialog";
import { X } from "lucide-react";
import { toast } from "sonner";
import { api, unwrapEnvelope } from "@/lib/api";

export function WalmartCredentialReplaceModal({ open, onOpenChange, onReplaced }) {
  const [clientId, setClientId] = useState("");
  const [clientSecret, setClientSecret] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open) {
      setClientId("");
      setClientSecret("");
      setError(null);
    }
  }, [open]);

  async function handleReplace() {
    setSubmitting(true);
    setError(null);
    try {
      const res = await api.walmartReplaceCredentials({
        client_id: clientId.trim(),
        client_secret: clientSecret,
      });
      const data = unwrapEnvelope(res);
      toast.success("Walmart credentials updated");
      onReplaced?.(data?.integration);
      onOpenChange(false);
    } catch (err) {
      setError(
        err?.response?.data?.detail ||
          "Walmart credentials could not be verified."
      );
    } finally {
      setClientSecret("");
      setSubmitting(false);
    }
  }

  if (!open) return null;

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-black/40 z-[92]" />
        <Dialog.Content
          className="integration-modal fixed left-1/2 top-1/2 z-[93] w-[min(480px,calc(100vw-24px))] max-h-[90dvh] overflow-y-auto -translate-x-1/2 -translate-y-1/2 bg-white rounded-panel border border-edge-subtle shadow-menu p-5"
          data-testid="walmart-credential-replace-modal"
        >
          <div className="flex items-start justify-between gap-3 mb-4">
            <Dialog.Title className="text-stitle text-ink-primary">
              Update credentials
            </Dialog.Title>
            <Dialog.Close className="p-1 rounded-ctrl hover:bg-surface-2">
              <X className="h-4 w-4" />
            </Dialog.Close>
          </div>

          <label className="block text-compact mb-3">
            <span className="text-ink-secondary">Client ID</span>
            <input
              autoComplete="off"
              className="mt-1 w-full h-9 px-3 rounded-ctrl border border-edge-subtle"
              value={clientId}
              onChange={(e) => setClientId(e.target.value)}
              data-testid="walmart-replace-client-id"
            />
          </label>

          <label className="block text-compact mb-3">
            <span className="text-ink-secondary">Client Secret</span>
            <input
              type="password"
              autoComplete="new-password"
              className="mt-1 w-full h-9 px-3 rounded-ctrl border border-edge-subtle"
              value={clientSecret}
              onChange={(e) => setClientSecret(e.target.value)}
              data-testid="walmart-replace-client-secret"
            />
          </label>

          {error && (
            <p className="text-compact text-red-600 mb-3" data-testid="walmart-replace-error">
              {error}
            </p>
          )}

          <div className="integration-modal__footer flex justify-end gap-2 pt-2">
            <button
              type="button"
              className="h-9 px-3 rounded-ctrl bg-signal-blue text-white text-compact disabled:opacity-50"
              disabled={
                submitting || !clientId.trim() || !clientSecret
              }
              onClick={handleReplace}
              data-testid="walmart-replace-submit"
            >
              {submitting ? "Testing…" : "Test & replace"}
            </button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
